import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Data, Route, RouterModule } from '@angular/router';
import { AuthService, DashboardStore, LoggerService, OrganisationService, UserService } from '@app/shared/data';
import { getEffectiveTier, SubscriptionTier, User, UserRole, UserType } from '@app/shared/domain';
import { NewOrganisationComponent } from '@app/shared/ui';
import { environment } from '@env';
import { documentId, where } from 'firebase/firestore';
import { MenuItem } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { MenuModule } from 'primeng/menu';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { combineLatest, distinctUntilChanged, map, Observable, of, startWith, switchMap } from 'rxjs';
import { routes } from '../app.routes';
import { AppStore } from '../app.store';

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.html',

  imports: [
    CommonModule,
    NgOptimizedImage,
    FormsModule,
    RouterModule,
    AvatarModule,
    ButtonModule,
    MenuModule,
    ToggleSwitchModule,
    TagModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardComponent implements OnInit {
  user$: Observable<User>;

  routeData$: Observable<Data>;

  menuItems$: Observable<MenuItem[]>;

  private readonly _dashboardStore = inject(DashboardStore);

  private readonly _appStore = inject(AppStore);

  private readonly _organisationService = inject(OrganisationService);

  private readonly _dialogService = inject(DialogService);

  private readonly _authService = inject(AuthService);

  private readonly _activatedRoute = inject(ActivatedRoute);

  private readonly _userService = inject(UserService);

  private readonly _destroyRef = inject(DestroyRef);

  private readonly _logger = inject(LoggerService);

  readonly logoPath: string = environment.businessInfo.logoPath;

  readonly businessName: string = environment.businessInfo.name;

  isSidebarOpen = this._dashboardStore.isSidebarOpen;

  readonly isDarkMode = computed(() => this._appStore.theme() === 'dark');

  readonly roleLabel = computed(() => {
    const role = this._dashboardStore.currentRole();
    if (role === null || role === undefined) {
      return null;
    }
    const labels: Record<number, string> = {
      [UserRole.SuperAdmin]: UserType.SuperAdmin,
      [UserRole.Owner]: UserType.Owner,
      [UserRole.Admin]: UserType.Admin,
      [UserRole.Collaborator]: UserType.Collaborator,
    };
    return labels[role] || null;
  });

  loadedOrg = this._dashboardStore.selectedOrganisationId;

  readonly isSettingsOpen = signal(false);

  currentOrganisation$ = toObservable(this._dashboardStore.selectedOrganisationId).pipe(
    takeUntilDestroyed(this._destroyRef),
    switchMap((orgId: string | null) => {
      if (!orgId) {
        return of(null);
      }
      return this._organisationService.getByID(orgId);
    }),
  );

  private readonly _baseMenuItems: MenuItem[] = [
    {
      label: 'Settings',
      icon: 'pi pi-cog',
      routerLink: '/dashboard/settings',
      routerLinkActiveOptions: { exact: true },
    },
    { separator: true },
    {
      label: 'Create Organisation',
      icon: 'pi pi-plus',
      command: (): void => {
        this.openCreateOrganisationDialog();
      },
    },
    { separator: true },
    {
      label: 'Profile',
      icon: 'pi pi-users',
      routerLink: '/dashboard/settings/profile',
      routerLinkActiveOptions: { exact: true },
    },
    {
      label: 'Sign out',
      icon: 'pi pi-sign-out',
      command: (): void => {
        this._authService.logout();
      },
    },
  ];

  ngOnInit(): void {
    this._logger.log('Initializing dashboard component');

    this.user$ = this._authService.user$;
    this.routeData$ = this._activatedRoute.data;

    // Optimize subscription pattern using combineLatest and switchMap
    this.menuItems$ = this._authService.user$.pipe(
      takeUntilDestroyed(this._destroyRef),
      switchMap((currentUser) => {
        if (!currentUser) {
          this._logger.log('No current user, returning base menu items');
          return of(this._baseMenuItems);
        }

        return this._userService.getByID(currentUser.id);
      }),
      distinctUntilChanged((prev, curr) => {
        // Only re-evaluate if user ID or roles have changed
        // Handle the case where startWith provides MenuItem[] as initial value
        if (!prev || !curr || Array.isArray(prev) || Array.isArray(curr)) {
          return false; // Always re-evaluate if we don't have proper User objects
        }
        return prev.id === curr.id && JSON.stringify(prev.roles) === JSON.stringify(curr.roles);
      }),
      switchMap((user: User) => {
        if (!user || this._dashboardStore.currentRole() === UserRole.SuperAdmin) {
          this._logger.log('User is super admin or not found, returning base menu items');
          return of(this._baseMenuItems);
        }

        const orgIds = Object.keys(user.roles).filter((key) => key !== 'global');

        if (orgIds.length === 0) {
          this._logger.log('No organizations found for user');
          return of(this._baseMenuItems);
        }

        // Combine current role and organizations data
        return combineLatest([
          this._authService
            .getCurrentRole(user.id)
            .pipe(distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))),
          this._organisationService.getAll(where(documentId(), 'in', orgIds)),
        ]).pipe(
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          map(([_currentRole, orgs]) => {
            this._logger.log('Building menu items for organizations', { orgCount: orgs?.length });

            if (!orgs || orgs.length === 0) {
              return this._baseMenuItems;
            }

            const orgMenuItems: MenuItem[] = orgs.map((org) => ({
              label: org.name,
              icon: org.avatar ? undefined : 'pi pi-building',
              styleClass: org.id === this._dashboardStore.selectedOrganisationId() ? '!text-primary-500' : '',
              state: {
                avatar: org.avatar || null,
                role: this._getRoleLabel(
                  user.roles instanceof Map ? user.roles.get(org.id) : (user.roles as Record<string, UserRole>)?.[org.id],
                ),
                tier: this._getTierLabel(getEffectiveTier(org)),
              },
              command: (): void => {
                this._logger.log('Switching to organization', { orgId: org.id });
                this._dashboardStore.setOrganisationId(org.id);
              },
            }));

            // Insert organisation items after the first separator
            const menuItems = [...this._baseMenuItems];
            const firstSeparatorIndex = menuItems.findIndex((item) => item.separator);
            if (firstSeparatorIndex !== -1) {
              menuItems.splice(firstSeparatorIndex + 1, 0, ...orgMenuItems, { separator: true });
            }

            return menuItems;
          }),
        );
      }),
      startWith(this._baseMenuItems),
      // Prevent re-rendering if menu items haven't changed
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
    );
  }

  openCreateOrganisationDialog(): void {
    this._dialogService.open(NewOrganisationComponent, {
      header: `Create ${this._organisationService.organisationAlias}`,
      width: '40vw',
      breakpoints: { '1199px': '75vw', '575px': '90vw' },
      closable: true,
      modal: true,
      dismissableMask: false,
    });
  }

  /**
   * Toggles the open state of the sidebar
   */
  toggleSidebar(): void {
    this._dashboardStore.toggleSidebar();
  }

  /**
   * Toggles the open state of the sidebar
   */
  closeSidebar(): void {
    this._dashboardStore.closeSidebar();
  }

  /**
   * Toggles dark mode
   */
  toggleDarkMode(): void {
    this._appStore.toggleTheme();
  }

  /**
   * Sets the sidebar menu based on routes for that user
   * @param role role of current user
   * @returns array of routes
   */
  private _getRoleLabel(role: UserRole | number | undefined): string | null {
    const labels: Record<number, string> = {
      [UserRole.SuperAdmin]: UserType.SuperAdmin,
      [UserRole.Owner]: UserType.Owner,
      [UserRole.Admin]: UserType.Admin,
      [UserRole.Collaborator]: UserType.Collaborator,
    };
    return role !== undefined && role !== null ? (labels[role as number] ?? null) : null;
  }

  private _getTierLabel(tier: SubscriptionTier | null | undefined): string | null {
    if (!tier) {
      return null;
    }
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  }

  getMenuForRole(role: UserRole): Route[] {
    // Find the route matching the given role
    const matchingRoute = routes.find((route) => route.path === 'dashboard' && route.data?.role === role);

    // Return the top-level children if the matching route is found, or an empty array otherwise
    return matchingRoute?.children.filter((route) => route.data?.icon !== undefined) || [];
  }
}
